import { useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Mail, CheckCircle2, Undo2, Send } from "lucide-react";

const STATUS = { new: "New", in_progress: "In progress", pending_approval: "Pending approval", resolved: "Resolved" };
const STATUS_CLASS = {
  new: "bg-[#F7E6DD] text-[#C79A3B]",
  in_progress: "bg-amber-100 text-amber-800",
  pending_approval: "bg-blue-100 text-blue-800",
  resolved: "bg-green-100 text-green-800",
};

export function ComplaintsPanel({ complaints, onReload, role }) {
  const isAdminLevel = role === "admin" || role === "senior_admin";
  return (
    <div data-testid="admin-complaints" className="space-y-4">
      {complaints.length === 0 && <div className="border border-[#E5E7EB] bg-white p-6 text-center text-[#6B7280] text-sm">No complaints received.</div>}
      {complaints.map(c => <ComplaintCard key={c.id} c={c} onReload={onReload} isAdminLevel={isAdminLevel} />)}
    </div>
  );
}

function ComplaintCard({ c, onReload, isAdminLevel }) {
  const [note, setNote] = useState("");
  const [subject, setSubject] = useState(`Re: your complaint (${c.category})`);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [showCompose, setShowCompose] = useState(false);

  const patch = async (payload, okMsg) => {
    try {
      await api.patch(`/admin/complaint/${c.id}`, payload);
      toast.success(okMsg || "Complaint updated");
      onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };

  const sendEmail = async () => {
    if (!subject.trim() || !body.trim()) { toast.error("Subject and message are required"); return; }
    setSending(true);
    try {
      const r = await api.post(`/admin/complaint/${c.id}/email`, { subject, body });
      toast.success(r.data.sent ? "Email sent to customer" : "Email could not be delivered — check Resend settings.");
      setBody(""); setShowCompose(false);
      onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send email"); }
    setSending(false);
  };

  return (
    <div data-testid={`complaint-card-${c.id}`} className="border border-[#E5E7EB] bg-white p-5">
      <div className="flex flex-wrap justify-between gap-3 items-start">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-bold">{c.name}</span>
            <span data-testid={`complaint-badge-${c.id}`} className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${STATUS_CLASS[c.status] || STATUS_CLASS.new}`}>{STATUS[c.status] || c.status}</span>
          </div>
          <div className="text-xs text-[#6B7280] mt-1">{c.email} · {c.phone || "no phone"} · {c.postcode || "—"} · Ref {c.reference || "—"}</div>
        </div>
        <div className="text-xs text-[#6B7280]">{new Date(c.created_at).toLocaleString("en-GB")}</div>
      </div>
      <div className="mt-3 label-eyebrow">{c.category}</div>
      <p className="mt-1 text-sm whitespace-pre-wrap">{c.message}</p>

      {/* Correspondence thread */}
      {(c.correspondence || []).length > 0 && (
        <div className="mt-4 border-t border-[#E5E7EB] pt-3 space-y-2" data-testid={`complaint-thread-${c.id}`}>
          <div className="label-eyebrow">Email correspondence</div>
          {c.correspondence.map((m, i) => (
            <div key={`${m.at || "corr"}-${i}`} className="bg-[#F4F4F4] border border-[#E5E7EB] p-3">
              <div className="text-[11px] text-[#6B7280] flex justify-between">
                <span>→ to {c.email} · <b>{m.by}</b>{m.sent === false && <span className="text-red-600"> (delivery failed)</span>}</span>
                <span>{new Date(m.at).toLocaleString("en-GB")}</span>
              </div>
              <div className="text-sm font-semibold mt-1">{m.subject}</div>
              <p className="text-sm whitespace-pre-wrap mt-1">{m.body}</p>
            </div>
          ))}
        </div>
      )}

      {/* Internal notes */}
      {(c.notes || []).length > 0 && (
        <div className="mt-3 border-t border-[#E5E7EB] pt-3 space-y-2">
          <div className="label-eyebrow">Internal notes</div>
          {c.notes.map((n, i) => (
            <div key={`${n.at || "note"}-${i}`} className="text-xs text-[#6B7280]"><b>{n.by}</b> · {new Date(n.at).toLocaleString("en-GB")}<br />{n.note}</div>
          ))}
        </div>
      )}

      {/* Actions */}
      {c.status !== "resolved" && (
        <div className="mt-4 border-t border-[#E5E7EB] pt-4 space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <div className="label-eyebrow mb-1">Status</div>
              <select data-testid={`complaint-status-${c.id}`} value={c.status === "pending_approval" ? "in_progress" : c.status}
                onChange={e => patch({ status: e.target.value })} className="border border-[#E5E7EB] bg-white px-3 py-2 text-sm">
                <option value="new">New</option>
                <option value="in_progress">In progress</option>
              </select>
            </div>
            <button data-testid={`complaint-email-toggle-${c.id}`} onClick={() => setShowCompose(s => !s)} className="de-btn-secondary text-xs inline-flex items-center gap-2"><Mail size={14} /> Email customer</button>

            {c.status !== "pending_approval" && (
              <button data-testid={`complaint-request-resolve-${c.id}`} onClick={() => patch({ status: "pending_approval" }, "Resolution requested — awaiting admin approval")} className="de-btn-secondary text-xs">Mark resolved (request approval)</button>
            )}
            {c.status === "pending_approval" && isAdminLevel && (
              <>
                <button data-testid={`complaint-approve-${c.id}`} onClick={() => patch({ status: "resolved" }, "Resolution approved — complaint closed")} className="de-btn-primary text-xs inline-flex items-center gap-2"><CheckCircle2 size={14} /> Approve resolution</button>
                <button data-testid={`complaint-sendback-${c.id}`} onClick={() => patch({ status: "in_progress" }, "Sent back to in progress")} className="de-btn-secondary text-xs inline-flex items-center gap-2"><Undo2 size={14} /> Send back</button>
              </>
            )}
            {c.status === "pending_approval" && !isAdminLevel && (
              <span className="text-xs text-blue-700 font-semibold self-center">Awaiting admin approval</span>
            )}
          </div>

          {showCompose && (
            <div className="bg-[#F4F4F4] border border-[#E5E7EB] p-3 space-y-2" data-testid={`complaint-compose-${c.id}`}>
              <input data-testid={`complaint-email-subject-${c.id}`} value={subject} onChange={e => setSubject(e.target.value)} className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" placeholder="Subject" />
              <textarea data-testid={`complaint-email-body-${c.id}`} rows={4} value={body} onChange={e => setBody(e.target.value)} className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" placeholder="Write your reply to the customer…" />
              <button data-testid={`complaint-email-send-${c.id}`} disabled={sending} onClick={sendEmail} className="de-btn-primary text-xs inline-flex items-center gap-2"><Send size={14} /> {sending ? "Sending…" : "Send email"}</button>
            </div>
          )}

          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <div className="label-eyebrow mb-1">Add internal note</div>
              <input data-testid={`complaint-note-${c.id}`} value={note} onChange={e => setNote(e.target.value)} className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" />
            </div>
            <button data-testid={`complaint-note-save-${c.id}`} disabled={!note.trim()} onClick={() => { patch({ note }); setNote(""); }} className="de-btn-secondary text-xs">Add note</button>
          </div>
        </div>
      )}

      {c.status === "resolved" && (
        <div className="mt-4 border-t border-[#E5E7EB] pt-3 text-xs text-green-700 font-semibold" data-testid={`complaint-resolved-${c.id}`}>
          Resolved{c.resolved_by ? ` by ${c.resolved_by}` : ""}{c.resolved_at ? ` · ${new Date(c.resolved_at).toLocaleString("en-GB")}` : ""}
        </div>
      )}
    </div>
  );
}
